import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import {
  ContentSection,
  DataSectionItem,
} from '@app/components/blocks/contentSection/ContentSection'
import useMediaQuery from '@app/util/useMediaQuery'
import { ParagraphCollapsible } from '@app/components/blocks/contentCollapsible/ParagraphCollapsible'
import { ViewInitiativeModel } from 'functions/app/initiative/view'
import { addressBuilder } from '@app/util/helpers'

export const Overview = ({
  initiative,
}: {
  initiative: ViewInitiativeModel
}) => {
  const isMobile = useMediaQuery()
  const t = useTranslations().app
  const translations = t.initiativePage
  const numberOfHomesOptions =
    t.dashboard.initiative.creation.numberOfHomesOptions
  const notSpecified = t.common.notSpecified
  const geo = initiative.location.geo
  const address =
    addressBuilder(initiative.location.address) ?? translations.unknownLocation

  const renderMap = geo.lat && geo.long && geo.lat !== 0 && geo.long !== 0

  const pageContentBlocks = initiative.contentBlocks

  const contactInformationListItems: DataSectionItem[] = [
    {
      label: translations.contactInformationKeys.contactPerson,
      content: initiative.contactFirstName
        ? `${initiative.contactFirstName} ${initiative.contactLastName}`
        : notSpecified,
      styling: 'text',
    },
  ]

  const featuresListItems: DataSectionItem[] = [
    {
      label: translations.overviewFeaturesKeys.location,
      content: address,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.phase,
      content: initiative.phase?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.developmentForm,
      content: initiative.developmentForm?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.numberOfHouseholds,
      content: initiative.numberOfHomes
        ? numberOfHomesOptions[initiative.numberOfHomes]
        : notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.livingEnvironment,
      content:
        initiative.livingEnvironments?.map((le) => le.name).join(', ') ||
        notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.targetAudience,
      content:
        initiative.targetAudiences?.map((ta) => ta.name).join(', ') ||
        notSpecified,
      styling: 'multi-line-text',
    },
    {
      label: translations.overviewFeaturesKeys.tags,
      content:
        initiative.interests?.map((interest) => interest.name).join(', ') ||
        notSpecified,
      styling: 'tag',
    },
  ]

  return (
    <Column gap={isMobile ? '2rem' : '3.5rem'} alignItems="stretch">
      <ParagraphCollapsible>{initiative.vision}</ParagraphCollapsible>

      {pageContentBlocks.map((block, index) => (
        <ContentSection
          key={block.id ?? index}
          section={{
            type: 'text',
            title: block.title,
            body: block.body,
            truncationThreshold: 0,
          }}
        />
      ))}

      {renderMap && (
        <Column alignItems="stretch">
          <ContentSection
            section={{
              type: 'location',
              title: translations.overviewTab.searchArea,
              subtitle: address,
              lat: geo.lat,
              long: geo.long,
              marker: 'initiative',
            }}
          />
        </Column>
      )}

      <ContentSection
        section={{
          type: 'data',
          title: translations.overviewTab.features,
          listItems: featuresListItems.map((item) => ({
            label: item.label,
            content: item.content,
            styling: item.styling,
          })) as DataSectionItem[],
        }}
      />
      <ContentSection
        section={{
          type: 'data',
          title: translations.overviewTab.contact,
          listItems: contactInformationListItems.map((item) => ({
            label: item.label,
            content: item.content,
            styling: item.styling,
          })) as DataSectionItem[],
        }}
      />
    </Column>
  )
}
